import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { CATALOG, CATALOG_VERSION, type CapabilityNodeKey } from '@/lib/capabilities/catalog';
import type { CapabilityScopeType } from '@/lib/capabilities/contracts';
import {
  resolveCapabilities,
  type OverrideRow,
} from '@/lib/capabilities/resolveCapabilities';
import type { WorkspaceEntitlementsSnapshot } from '@/lib/billing/types';
import {
  getCapabilityConfigurationMeta,
  getWorkspaceCapabilities,
  getWorkspaceCapabilityBilling,
  getWorkspaceFeatureSettings,
  editionDisplayName,
  listEditions,
  listWorkspaces,
  setWorkspaceCapability,
  setWorkspaceEdition,
  type CapabilityConfigurationMetaRow,
  type Edition,
  type WorkspaceCapabilityBillingRow,
  type WorkspaceCapabilityRow,
  type WorkspaceFeatureSettingRow,
  type WorkspaceRow,
} from './api';
import WorkspaceAccess from './WorkspaceAccess';
import WorkspacePricing from './WorkspacePricing';

const VALID_SCOPES = new Set<CapabilityScopeType>([
  'global',
  'environment',
  'edition',
  'beta_programme',
  'industry',
  'journey_pack',
  'workspace',
  'role',
  'user',
  'internal_dev',
]);

function asOverride(row: WorkspaceCapabilityRow): OverrideRow | null {
  if (!VALID_SCOPES.has(row.scope_type as CapabilityScopeType)) return null;
  if (!(row.capability_key in CATALOG)) return null;
  return {
    scopeType: row.scope_type as CapabilityScopeType,
    scopeId: row.scope_id,
    capabilityKey: row.capability_key as CapabilityNodeKey,
    effect: row.effect,
  };
}

function entitlementsForPreview(
  accountId: string,
  billing: WorkspaceCapabilityBillingRow,
): WorkspaceEntitlementsSnapshot {
  return {
    account_id: accountId,
    plan_key: 'assets',
    plan_display_name: 'Assets',
    subscription_status: 'admin-preview',
    billing_provider: 'admin-preview',
    included_ai_credits_monthly: null,
    ai_credits_balance: 0,
    automation_credits_balance: 0,
    generic_credits_balance: 0,
    unlimited_ai_credits: false,
    billable_active_assets: 0,
    max_billable_assets: null,
    within_asset_limit: true,
    feature_entitlements: billing.feature_entitlements,
    asset_pricing_bands: [],
    access_state: billing.access_state,
  } as unknown as WorkspaceEntitlementsSnapshot;
}

function editionLabel(value: string | null, editions: Edition[]): string {
  if (value == null) return 'Not set';
  return editionDisplayName(editions.find((edition) => edition.plan_key === value), value);
}

export default function ControlPlane({ toast }: { toast: (message: string) => void }) {
  const [workspaces, setWorkspaces] = useState<WorkspaceRow[]>([]);
  // Developer (tracker 47): narrow the list to the internal test workspaces.
  const [onlyInternal, setOnlyInternal] = useState(false);
  const [editions, setEditions] = useState<Edition[]>([]);
  const [accountId, setAccountId] = useState('');
  const [rows, setRows] = useState<WorkspaceCapabilityRow[]>([]);
  const [meta, setMeta] = useState<CapabilityConfigurationMetaRow | null>(null);
  const [billing, setBilling] = useState<WorkspaceCapabilityBillingRow | null>(null);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingEdition, setSavingEdition] = useState(false);
  // Tracker 47 dev-10: features switched on or off for this one workspace.
  const [featureSettings, setFeatureSettings] = useState<WorkspaceFeatureSettingRow[]>([]);
  const [savingFeature, setSavingFeature] = useState<string | null>(null);

  const loadWorkspace = useCallback(async (nextAccountId: string) => {
    if (!nextAccountId) return;
    setLoading(true);
    try {
      const [nextRows, nextMeta, nextBilling, nextSettings] = await Promise.all([
        getWorkspaceCapabilities(nextAccountId),
        getCapabilityConfigurationMeta(nextAccountId),
        getWorkspaceCapabilityBilling(nextAccountId),
        getWorkspaceFeatureSettings(nextAccountId).catch(() => [] as WorkspaceFeatureSettingRow[]),
      ]);
      setRows(nextRows);
      setMeta(nextMeta);
      setBilling(nextBilling);
      setFeatureSettings(nextSettings);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void Promise.all([listWorkspaces(), listEditions()])
      .then(([nextWorkspaces, nextEditions]) => {
        setWorkspaces(nextWorkspaces);
        setEditions(nextEditions);
        const first = nextWorkspaces[0]?.account_id ?? '';
        setAccountId(first);
        if (first) return loadWorkspace(first);
        setLoading(false);
      })
      .catch((error: Error) => {
        setLoading(false);
        toast(`Control plane failed to load: ${error.message}`);
      });
  }, [loadWorkspace, toast]);

  const preview = useMemo(() => {
    if (!billing) return null;
    const mapped = rows.map(asOverride).filter((row): row is OverrideRow => row != null);
    return resolveCapabilities({
      entitlements: entitlementsForPreview(accountId, billing),
      editionGrants: mapped.filter((row) => row.scopeType === 'edition'),
      overrides: mapped.filter((row) => row.scopeType !== 'edition'),
      environment: 'production',
      isInternalContext: false,
      betaProgrammes: [],
    });
  }, [accountId, billing, rows]);

  const matchingNodes = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return Object.values(CATALOG).filter(
      (node) =>
        normalized.length === 0 ||
        node.title.toLowerCase().includes(normalized) ||
        node.key.toLowerCase().includes(normalized),
    );
  }, [query]);

  const selectedWorkspace = workspaces.find((workspace) => workspace.account_id === accountId);

  const changeEdition = async (nextEdition: string) => {
    if (!accountId) return;
    setSavingEdition(true);
    try {
      const result = await setWorkspaceEdition(accountId, nextEdition || null);
      await loadWorkspace(accountId);
      setWorkspaces((current) =>
        current.map((workspace) =>
          workspace.account_id === accountId
            ? { ...workspace, edition_key: result.edition_key }
            : workspace,
        ),
      );
      toast(`Edition saved. Capability revision is now ${result.config_revision}.`);
    } catch (error) {
      toast(`Edition update failed: ${(error as Error).message}`);
    } finally {
      setSavingEdition(false);
    }
  };

  const changeFeature = async (capabilityKey: string, title: string, next: '' | 'enable' | 'disable') => {
    if (!accountId) return;
    const effect = next === '' ? null : next;
    let reason: string | null = null;
    if (effect) {
      reason = window.prompt(
        `${effect === 'enable' ? 'Switch on' : 'Switch off'} ${title} for ${selectedWorkspace?.name || 'this workspace'} only? Say why (kept with the setting).`,
      );
      if (reason == null) return;
      if (reason.trim().length < 3) {
        toast('Say why in a few words, so the setting can be understood later.');
        return;
      }
    }
    setSavingFeature(capabilityKey);
    try {
      await setWorkspaceCapability(accountId, capabilityKey, effect, reason?.trim() ?? null);
      await loadWorkspace(accountId);
      setWorkspaces((current) =>
        current.map((workspace) => {
          if (workspace.account_id !== accountId) return workspace;
          const had = featureSettings.some((row) => row.capability_key === capabilityKey && row.set_by_assetwrx);
          const delta = (effect ? 1 : 0) - (had ? 1 : 0);
          return { ...workspace, feature_settings: Math.max(0, (workspace.feature_settings ?? 0) + delta) };
        }),
      );
      toast(effect ? `${title} is ${effect === 'enable' ? 'on' : 'off'} for this workspace.` : `${title} follows the plan again.`);
    } catch (error) {
      toast(`Feature setting failed: ${(error as Error).message}`);
    } finally {
      setSavingFeature(null);
    }
  };

  if (loading && workspaces.length === 0) return <p className="muted">Loading control plane…</p>;
  if (workspaces.length === 0) return <p className="muted">No workspaces were returned.</p>;

  return (
    <div>
      {/* Collapsed by default: a reference the owner wants a few times a month,
          findable in one line without taxing the capability work below it. */}
      <WorkspacePricing toast={toast} />

      <div className="card" style={{ marginBottom: 18 }}>
        <h2 style={{ marginTop: 0 }}>Workspace authority</h2>
        <p className="sub">
          Every workspace is on an edition (new workspaces start on Free). The edition decides which
          features the workspace gets, through the Matrix; overrides and billing floors still apply on top.
          To give one workspace a feature its plan does not have (or take one away), use the
          Workspace override column below: it beats the plan, a global hold-back still beats it,
          and the owner sees "Set by AssetWRX".
        </p>
        <div className="row" style={{ alignItems: 'end' }}>
          <div style={{ flex: '1 1 320px' }}>
            <label>Workspace</label>
            <select
              value={accountId}
              onChange={(event) => {
                const next = event.target.value;
                setAccountId(next);
                void loadWorkspace(next).catch((error: Error) => toast(error.message));
              }}
            >
              {workspaces
                .filter(
                  (workspace) =>
                    !onlyInternal ||
                    workspace.account_id === accountId ||
                    editions.some((e) => e.is_internal && e.plan_key === workspace.edition_key)
                )
                .map((workspace) => (
                <option key={workspace.account_id} value={workspace.account_id}>
                  {workspace.name || workspace.account_id} · {editionLabel(workspace.edition_key, editions)}
                  {workspace.feature_settings ? ` · ${workspace.feature_settings} feature setting${workspace.feature_settings === 1 ? '' : 's'}` : ''}
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: '0 1 260px' }}>
            <label>Workspace edition</label>
            <select
              value={meta?.edition_key ?? ''}
              disabled={savingEdition || loading}
              onChange={(event) => void changeEdition(event.target.value)}
            >
              {/* Every workspace is on an edition (tracker 34.10): there is no "none" to pick. */}
              {!meta?.edition_key ? <option value="" disabled>Choose an edition</option> : null}
              {editions.map((edition) => (
                <option key={edition.plan_key} value={edition.plan_key}>{editionDisplayName(edition)}</option>
              ))}
            </select>
          </div>
          <label style={{ flex: '0 1 auto', display: 'flex', gap: 6, alignItems: 'center', fontWeight: 400 }}>
            <input type="checkbox" checked={onlyInternal} onChange={(event) => setOnlyInternal(event.target.checked)} />
            Only Developer workspaces
          </label>
          <button className="btn" onClick={() => void loadWorkspace(accountId)} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh resolution'}
          </button>
        </div>
        <div className="legend" style={{ marginTop: 14 }}>
          <span>Workspace: <strong>{selectedWorkspace?.name || accountId}</strong></span>
          <span>Edition: <strong>{editionLabel(meta?.edition_key ?? null, editions)}</strong></span>
          <span>Configuration revision: <strong>{String(meta?.config_revision ?? '—')}</strong></span>
          <span>Catalog: <strong>{CATALOG_VERSION}</strong></span>
          <span>Billing access: <strong>{billing?.access_state ?? '—'}</strong></span>
        </div>
      </div>

      {accountId ? (
        <WorkspaceAccess accountId={accountId} editions={editions} toast={toast} onChanged={() => void loadWorkspace(accountId)} />
      ) : null}

      <div className="row" style={{ marginBottom: 12 }}>
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search resolved capabilities…"
          style={{ maxWidth: 380 }}
        />
      </div>

      <div className="matrix-scroll">
        <table className="matrix">
          <thead>
            <tr>
              <th>Capability</th>
              <th className="col-ed">Resolved</th>
              <th>Winning source</th>
              <th>Blocking dependency</th>
              <th>Billing floor</th>
              <th>Workspace override</th>
              <th>Edition grant</th>
              <th>Revision</th>
            </tr>
          </thead>
          <tbody>
            {matchingNodes.map((node) => {
              const result = preview?.byKey[node.key as CapabilityNodeKey];
              const workspaceOverride = rows.find(
                (row) => row.scope_type === 'workspace' && row.capability_key === node.key,
              );
              const setting = featureSettings.find((row) => row.capability_key === node.key);
              const editionGrant = rows.find(
                (row) => row.source === 'grant' && row.capability_key === node.key,
              );
              return (
                <tr key={node.key}>
                  <td>
                    <div className="feat-name">{node.title}</div>
                    <div className="feat-key">{node.key}</div>
                  </td>
                  <td className="col-ed">
                    <span className={`cell ${result?.enabled ? 'on' : 'off'}`} style={{ cursor: 'default' }}>
                      {result?.enabled ? '✓' : '·'}
                    </span>
                  </td>
                  <td>{result?.source ?? '—'}</td>
                  <td>{result?.source === 'dependency' ? result.reason?.replace('blocked by ', '') : '—'}</td>
                  <td>{result?.source === 'billing_floor' ? result.reason ?? 'Billing entitlement/access state' : node.layer === 'capability' && node.billingFeatureKey ? `Eligible: ${node.billingFeatureKey}` : '—'}</td>
                  <td>
                    {node.alwaysIncluded ? (
                      '—'
                    ) : (
                      <>
                        <select
                          value={setting?.set_by_assetwrx ? setting.effect : ''}
                          disabled={loading || savingFeature === node.key}
                          onChange={(event) =>
                            void changeFeature(node.key, node.title, event.target.value as '' | 'enable' | 'disable')
                          }
                          style={{ minWidth: 170 }}
                        >
                          <option value="">Plan default</option>
                          <option value="enable">On for this workspace</option>
                          <option value="disable">Off for this workspace</option>
                        </select>
                        {setting?.set_by_assetwrx ? (
                          <div className="feat-key">
                            {setting.reason ?? ''}
                            {setting.set_by_email ? ` · ${setting.set_by_email}` : ''}
                            {` · ${new Date(setting.set_at).toLocaleDateString()}`}
                          </div>
                        ) : workspaceOverride ? (
                          <div className="feat-key">
                            Owner set {workspaceOverride.effect === 'enable' ? 'on' : 'off'}
                            {workspaceOverride.reason ? ` · ${workspaceOverride.reason}` : ''}
                          </div>
                        ) : null}
                      </>
                    )}
                  </td>
                  <td>{editionGrant ? `${editionGrant.effect} · ${editionGrant.scope_id}` : '—'}</td>
                  <td>{String(meta?.config_revision ?? '—')}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
